import { useState, useEffect } from 'react';
import { Smartphone, Monitor, Trash2, Loader2, Shield, CheckCircle, AlertCircle } from 'lucide-react';
import { getUser, authenticatedFetch } from '@/lib/auth';
import { formatDate } from '@/utils/date';
import { useToast, useConfirm } from './ui';

interface TrustedDevice {
  id: string;
  user_id: string;
  device_fingerprint: string;
  device_name: string;
  last_used_at: string;
  created_at: string;
}

export default function DeviceManagement() {
  const [devices, setDevices] = useState<TrustedDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revoking, setRevoking] = useState<string | null>(null);
  const toast = useToast();
  const { confirm } = useConfirm();

  useEffect(() => {
    loadDevices();
  }, []);

  const loadDevices = async () => {
    try {
      const user = getUser();
      if (!user || !user.id) throw new Error('Not authenticated');

      const response = await authenticatedFetch('/api/auth/get-trusted-devices', {
        method: 'POST',
        body: JSON.stringify({
          user_id: user.id,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to load devices');
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to load devices');
      }

      setDevices(data.devices || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (deviceId: string) => {
    const confirmed = await confirm({
      title: 'Fjern enhet',
      message: 'Er du sikker på at du vil fjerne tillit til denne enheten? Du må verifisere enheten på nytt neste gang du logger inn.',
      confirmLabel: 'Fjern enhet',
      cancelLabel: 'Avbryt',
      variant: 'warning'
    });

    if (!confirmed) return;

    setRevoking(deviceId);
    try {
      const user = getUser();
      if (!user || !user.id) throw new Error('Not authenticated');

      const response = await authenticatedFetch('/api/auth/revoke-device', {
        method: 'POST',
        body: JSON.stringify({
          user_id: user.id,
          device_id: deviceId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to revoke device');
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to revoke device');
      }

      setDevices(devices.filter(d => d.id !== deviceId));
      toast.showSuccess('Enhet fjernet');
    } catch (err: any) {
      toast.showError('Feil ved fjerning: ' + err.message);
    } finally {
      setRevoking(null);
    }
  };

  const handleRevokeAll = async () => {
    const confirmed = await confirm({
      title: 'Fjern alle enheter',
      message: 'Er du sikker på at du vil fjerne ALLE pålitelige enheter? Du må verifisere på nytt neste gang du logger inn fra hvilken som helst enhet.',
      confirmLabel: 'Fjern alle',
      cancelLabel: 'Avbryt',
      variant: 'danger'
    });

    if (!confirmed) return;

    setRevoking('all');
    try {
      const user = getUser();
      if (!user || !user.id) throw new Error('Not authenticated');

      // Revoke all devices one by one
      for (const device of devices) {
        const response = await authenticatedFetch('/api/auth/revoke-device', {
          method: 'POST',
          body: JSON.stringify({
            user_id: user.id,
            device_id: device.id,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to revoke device');
        }

        const data = await response.json();
        if (!data.success) {
          throw new Error(data.error || 'Failed to revoke device');
        }
      }

      setDevices([]);
      toast.showSuccess('Alle enheter fjernet');
    } catch (err: any) {
      toast.showError('Feil ved fjerning: ' + err.message);
      loadDevices(); // Reload to get current state
    } finally {
      setRevoking(null);
    }
  };

  const getDeviceIcon = (deviceName: string) => {
    const name = deviceName.toLowerCase();
    if (name.includes('mobile') || name.includes('phone') || name.includes('android') || name.includes('ios')) {
      return <Smartphone size={24} />;
    }
    return <Monitor size={24} />;
  };

  const isCurrentDevice = (_deviceFingerprint: string) => {
    // Check if this is the current device
    // This is a simplified check - in production, you'd compare with current device fingerprint
    return false; // Simplified for now
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="animate-spin" size={48} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Enhetsbehandling</h1>
        <p className="text-gray-600 mt-2">
          Administrer dine pålitelige enheter. Du vil bli bedt om å verifisere nye enheter ved innlogging.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Info Card */}
      <div className="card mb-6 bg-kf-light">
        <div className="flex items-start gap-3">
          <Shield className="text-kf-primary flex-shrink-0" size={24} />
          <div>
            <h3 className="font-semibold text-kf-dark mb-1">Om pålitelige enheter</h3>
            <p className="text-sm text-kf-primary">
              For å øke sikkerheten, må du verifisere nye enheter når du logger inn første gang.
              Enheter du har verifisert vil bli husket, og du slipper å verifisere på nytt.
              Du kan fjerne enheter du ikke lenger stoler på eller bruker.
            </p>
          </div>
        </div>
      </div>

      {/* Devices List */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Dine pålitelige enheter ({devices.length})</h2>
          {devices.length > 1 && (
            <button
              onClick={handleRevokeAll}
              disabled={revoking !== null}
              className="btn btn-danger text-sm flex items-center gap-2"
            >
              {revoking === 'all' ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <Trash2 size={16} />
              )}
              Fjern alle
            </button>
          )}
        </div>

        {devices.length === 0 ? (
          <div className="text-center py-12">
            <Shield className="mx-auto mb-4 text-gray-400" size={64} />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Ingen pålitelige enheter</h3>
            <p className="text-gray-600">
              Du har ikke noen pålitelige enheter registrert ennå.
              Når du logger inn og verifiserer en enhet, vil den vises her.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {devices.map((device) => (
              <div
                key={device.id}
                className="border border-gray-200 rounded-lg p-4 hover:border-kf-primary transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="text-kf-primary mt-1">
                      {getDeviceIcon(device.device_name)}
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{device.device_name}</h3>
                        {isCurrentDevice(device.device_fingerprint) && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded-full flex items-center gap-1">
                            <CheckCircle size={12} />
                            Denne enheten
                          </span>
                        )}
                      </div>

                      <div className="text-sm text-gray-600 space-y-1">
                        <p>
                          <span className="font-medium">Sist brukt:</span>{' '}
                          {formatDate(device.last_used_at, 'dd.MM.yyyy HH:mm')}
                        </p>
                        <p>
                          <span className="font-medium">Lagt til:</span>{' '}
                          {formatDate(device.created_at, 'dd.MM.yyyy')}
                        </p>
                        <p className="text-xs text-gray-500 font-mono">
                          ID: {device.device_fingerprint.substring(0, 16)}...
                        </p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => handleRevoke(device.id)}
                    disabled={revoking === device.id}
                    className="btn btn-danger p-2 flex items-center gap-2"
                    title="Fjern tillit til enhet"
                  >
                    {revoking === device.id ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      <Trash2 size={18} />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Security Tips */}
      <div className="card mt-6 bg-kf-accent-20 border-kf-accent-40">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-kf-warning flex-shrink-0" size={24} />
          <div>
            <h3 className="font-semibold text-kf-accent mb-2">Sikkerhetstips</h3>
            <ul className="text-sm text-kf-accent space-y-1 list-disc list-inside">
              <li>Fjern enheter du ikke lenger bruker eller har tilgang til</li>
              <li>Hvis du ser en enhet du ikke kjenner igjen, fjern den umiddelbart</li>
              <li>Når du logger inn fra en offentlig eller delt datamaskin, velg "Ikke husk denne enheten"</li>
              <li>Hold alltid programvaren på enhetene dine oppdatert</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Help Section */}
      {devices.length > 0 && (
        <div className="mt-6 card bg-gray-50">
          <h3 className="font-semibold text-gray-900 mb-2">Trenger du hjelp?</h3>
          <p className="text-sm text-gray-700">
            Hvis du har mistet tilgang til alle dine enheter, eller trenger å tilbakestille
            enhetsverifiseringen din, ta kontakt med systemadministrator.
          </p>
        </div>
      )}
    </div>
  );
}
